import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {Box, Typography} from '@mui/material'
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined'
import {useCookies} from 'react-cookie'
import {useHistory} from 'react-router-dom'
import DataTable from 'src/components-new/data/DataTable'
import {ColumnDef, PaginationState} from 'src/components-new/data/DataTable'
import Filters, {FilterItem, FilterResult} from 'src/components-new/data/Filters'
import SearchBar from 'src/components-new/inputs/SearchBar'
import StatusPill from 'src/components-new/primitives/StatusPill'
import InitialsAvatar from 'src/components-new/primitives/InitialsAvatar'
import {STATUS, PATHS, sortPatientsByStateAndName, sortPatientStateOptions} from 'src/constants/constants'
import {NEUTRAL_SCALE} from 'src/theme-new/tokens'
import {useAppSelector} from 'src/hooks'
import apiClient from 'src/services/apiClient'

export interface PatientRecord {
  _id: string
  firstName?: string
  lastName?: string
  email?: string
  cellPhoneNumber?: string
  phoneNumber?: string
  address?: {city?: string}
  state?: string
  scope?: string
  [key: string]: any
}

const ROWS_PER_PAGE = 10
// The backend has no free-text OR search across firstName/lastName/email, and DataTable
// expects the caller to hand it an already-paginated page — so search and pagination both
// happen client-side over the full result set. 200 is the backend's hard cap on page size
// (the UMS proxy call underneath rejects `_l` > 200 with a 400), so it's also the most we
// can fetch in one request — a PSP with more patients than that won't be fully searchable
// client-side without also paging server-side while a search is active.
const FETCH_SIZE = 200

function getStateColor(state: string | undefined): 'success' | 'warning' | 'error' | 'info' | 'default' {
  if (!state) return 'default'
  if (state === 'ACTIVE' || state === 'Attivo' || state === 'ACTIVE_DELIVERY') return 'success'
  if (state === 'SUSPENDED') return 'warning'
  if (state === 'EXITED') return 'error'
  return 'default'
}

// Label dictionary sourced strictly from this PSP's own filter config (never from
// another PSP's list) — see withDiscoveredStateOptions below for how it's completed.
export function getStateLabel(state: string | undefined, labelMap: Record<string, string>): string {
  if (!state) return '—'
  return labelMap[state] ?? state
}

// A muted, low-emphasis tag for the patient's program/scope — deliberately understated
// (small neutral icon + text, no color, no uppercase label block) so it doesn't compete
// with the STATO pill. A single generic icon is used for every program on purpose: a
// per-program logo would need every PSP's icon loaded just to render this one line.
function ProgramTag({scope}: {scope?: string}): JSX.Element | null {
  if (!scope) return null
  return (
    <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25}}>
      <LabelOutlinedIcon sx={{fontSize: 13, color: NEUTRAL_SCALE[500]}} />
      <Typography sx={{fontSize: 12, color: NEUTRAL_SCALE[500]}}>{scope}</Typography>
    </Box>
  )
}

// Fields absorbed into the composite "Paziente" identity cell (avatar + name) rather
// than rendered as their own column — but each one only shows up there if the PSP
// config actually lists it, so a PSP that omits e.g. "firstName" never leaks it in.
const PAZIENTE_NAME_IDS = new Set(['firstName', 'lastName'])
// Rendered inline in the "Paziente" cell (via ProgramTag) instead of as its own
// labeled body block — kept out of the generic extraCols fallback below, and only
// shown when one of these ids is present in the PSP config.
const PROGRAM_COLUMN_IDS = new Set(['scope', 'programma', 'program'])

function getInitials(row: PatientRecord, showFirstName: boolean, showLastName: boolean): string {
  const combined = `${showFirstName ? row.firstName?.[0] ?? '' : ''}${showLastName ? row.lastName?.[0] ?? '' : ''}`
  return (combined || row.email?.[0] || '?').toUpperCase()
}

function getDisplayName(row: PatientRecord, showFirstName: boolean, showLastName: boolean): string {
  // Surname first, then first name — matches the list's sort order (state, then
  // surname, then first name), so rows within the same state visibly read as
  // alphabetical rather than looking shuffled.
  const parts = [showLastName ? row.lastName : undefined, showFirstName ? row.firstName : undefined].filter(Boolean)
  return parts.join(' ') || row.email || '—'
}

// Builds the table columns strictly from what the PSP config lists in
// `patientsConfigurations.columns` — every attribute (name, email, program, stato...)
// must be present there to appear at all, matching the legacy behavior where
// TableCustom rendered exactly one cell per configured column id and nothing else.
// The one exception is the "Paziente" identity cell (avatar + name), which always
// renders as the row's anchor/link target — but the name/program pieces inside it
// are still gated on the matching column id being configured.
function buildColumnsFromPsp(
  pspColumns: {id: string; label: string}[],
  stateLabelMap: Record<string, string>,
): ColumnDef<PatientRecord>[] {
  const pspIds = new Set(pspColumns.map(col => col.id))
  const showFirstName = pspIds.has('firstName')
  const showLastName = pspIds.has('lastName')
  const showProgram = [...PROGRAM_COLUMN_IDS].some(id => pspIds.has(id))

  const pazienteCol: ColumnDef<PatientRecord> = {
    key: 'paziente',
    header: 'Paziente',
    mobileCardHeader: true,
    render: (row) => (
      <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
        <InitialsAvatar initials={getInitials(row, showFirstName, showLastName)} />
        <Box sx={{minWidth: 0}}>
          <Typography variant="body2" fontWeight={600}>
            {getDisplayName(row, showFirstName, showLastName)}
          </Typography>
          {showProgram && <ProgramTag scope={row.scope} />}
        </Box>
      </Box>
    ),
  }

  const extraCols: ColumnDef<PatientRecord>[] = pspColumns
    .filter(col => !PAZIENTE_NAME_IDS.has(col.id) && !PROGRAM_COLUMN_IDS.has(col.id))
    .map(col => {
      if (col.id === 'state') {
        return {
          key: col.id,
          header: col.label,
          mobileCardBadge: true,
          render: (row: PatientRecord) => (
            <StatusPill label={getStateLabel(row.state, stateLabelMap)} color={getStateColor(row.state)} />
          ),
        }
      }
      if (col.id === 'cellPhoneNumber' || col.id === 'phoneNumber') {
        return {
          key: col.id,
          header: col.label,
          render: (row: PatientRecord) => (
            <Typography variant="body2">{row.cellPhoneNumber ?? row.phoneNumber ?? '—'}</Typography>
          ),
        }
      }
      if (col.id === 'email') {
        // Its own column (not merged into the "Paziente" cell) — selecting/copying the
        // email used to also select the name text next to it and register as a click
        // on the row, immediately navigating away before the copy could complete.
        return {
          key: col.id,
          header: col.label,
          render: (row: PatientRecord) => (
            <Typography variant="body2" sx={{wordBreak: 'break-all'}}>{row.email ?? '—'}</Typography>
          ),
        }
      }
      return {
        key: col.id,
        header: col.label,
        render: (row: PatientRecord) => {
          const val = col.id.split('.').reduce((obj: any, k) => obj?.[k], row)
          return <Typography variant="body2">{val ?? '—'}</Typography>
        },
      }
    })

  return [pazienteCol, ...extraCols]
}

// The PSP config's "state" filter options can lag behind the full lifecycle enum (e.g.
// omit TO_ACTIVE/"Da attivare"), leaving a row with a state the filter has no option to
// select. Earlier this backfilled the gap from a shared, all-PSPs constant — but that
// constant only ever reflected one PSP's (tutor's) states, so every other PSP ended up
// with tutor's states leaking into its own filter. Instead, backfill only from states
// actually seen in *this* PSP's own fetched patients, and expose them raw (value used
// as its own label, no translation) rather than silently borrowing another PSP's
// wording — a raw enum value in the dropdown is an obvious, visible signal that this
// PSP's config is missing that state, prompting a fix there instead of masking it here.
// Any option the PSP *did* configure is kept as-is, including custom labels (e.g.
// "Drop-Out"); all options are then ordered by PATIENT_STATE_ORDER, same as the rows.
export function withDiscoveredStateOptions(filters: FilterItem[], patients: PatientRecord[]): FilterItem[] {
  return filters.map(filter => {
    if (filter.id !== 'state') return filter
    const configured = filter.options ?? []
    const configuredValues = new Set(configured.map(option => option.value))
    const discovered = Array.from(new Set(
      patients
        .map(patient => patient.state)
        .filter((state): state is string => Boolean(state) && !configuredValues.has(state as string)),
    ))
    const extra = discovered.map(value => ({value, label: value}))
    return {...filter, options: sortPatientStateOptions([...configured, ...extra])}
  })
}

// Single label dictionary for patient state, shared by the filter dropdown and the
// table's STATO column — sourced only from this PSP's own (possibly backfilled) filter
// options, never from another PSP's list.
export function buildStateLabelMap(filters: FilterItem[]): Record<string, string> {
  const stateOptions = filters.find(filter => filter.id === 'state')?.options ?? []
  return Object.fromEntries(stateOptions.map(option => [option.value, option.label]))
}

export default function PatientsListNew(): JSX.Element {
  const pspPatientsConfigurations = useAppSelector(state => state.pspConfigReducer.data.patientsConfigurations)
  const currentScope = useAppSelector(state => state.pspConfigReducer.data.scope)
  const history = useHistory()
  const [cookies] = useCookies(['tokens'])

  const [patients, setPatients] = useState<PatientRecord[]>([])
  const [loadStatus, setLoadStatus] = useState<string>(STATUS.PENDING)
  const [pagination, setPagination] = useState<PaginationState>({page: 0, rowsPerPage: ROWS_PER_PAGE})
  const [activeFilters, setActiveFilters] = useState<FilterResult[]>([])
  const [search, setSearch] = useState<string>('')
  // Programs a patient can belong to — needed to cross-portal-redirect when a row's
  // scope differs from the current PSP (mirrors legacy src/pages/patients/Patients.tsx).
  const [programs, setPrograms] = useState<{scope: string; url: string}[]>([])

  const filterConfig: FilterItem[] = useMemo(
    () => withDiscoveredStateOptions(pspPatientsConfigurations?.filters ?? [], patients),
    [pspPatientsConfigurations, patients],
  )
  const stateLabelMap = useMemo(() => buildStateLabelMap(filterConfig), [filterConfig])

  useEffect(() => {
    apiClient.getPrograms().then((data: any) => {
      if (Array.isArray(data)) setPrograms(data)
    }).catch(() => {})
  }, [])

  const fetchPatients = useCallback(() => {
    // activeFilters is already {property, type, value}[] — send it as-is. The `type`
    // field is required by the backend to pick the right Mongo operator per filter
    // (rangePicker -> $gte/$lt, select -> $eq, checkbox -> $in, otherwise $regex);
    // stripping it made every filter fall through to the text-search branch.
    apiClient.postPatientsSearch(activeFilters, setLoadStatus, {
      page: 0,
      rowsPerPage: FETCH_SIZE,
      order: [],
    })
      .then((data: any) => {
        if (Array.isArray(data)) setPatients(data)
      })
      .catch(() => setLoadStatus(STATUS.REJECTED))
  }, [activeFilters])

  useEffect(() => {
    fetchPatients()
  }, [fetchPatients])

  const handleFiltersSubmit = (values: FilterResult[]) => {
    setActiveFilters(values)
    setPagination(prev => ({...prev, page: 0}))
  }

  const handleSearchChange = (value: string) => {
    setSearch(value)
    setPagination(prev => ({...prev, page: 0}))
  }

  // Search happens client-side over the full fetched set (not just the current page):
  // the backend's filter endpoint can't OR a query across firstName/lastName/email.
  const filteredPatients = search.trim()
    ? patients.filter(p => {
        const fullName = `${p.firstName ?? ''} ${p.lastName ?? ''}`.toLowerCase()
        const email = (p.email ?? '').toLowerCase()
        const q = search.toLowerCase()
        return fullName.includes(q) || email.includes(q)
      })
    : patients

  // Default ordering: active patients first (see PATIENT_STATE_ORDER), then surname/name.
  const sortedPatients = sortPatientsByStateAndName(filteredPatients)

  const pagedPatients = sortedPatients.slice(
    pagination.page * pagination.rowsPerPage,
    (pagination.page + 1) * pagination.rowsPerPage,
  )

  const pspColumns: {id: string; label: string}[] = pspPatientsConfigurations?.columns ?? []
  const columns: ColumnDef<PatientRecord>[] = buildColumnsFromPsp(pspColumns, stateLabelMap)

  // A patient can belong to a different program/scope than the one currently loaded
  // (e.g. a tutor managing patients enrolled in multiple PSPs). In that case the row
  // must not be opened via local routing — it lives on that program's own portal.
  const handleRowClick = (row: PatientRecord) => {
    if (!row.scope || row.scope === currentScope) {
      history.push(PATHS.PATIENTS + '/' + row._id)
      return
    }
    const host = programs.find(program => program.scope === row.scope)?.url ?? window.location.host
    const patientUrl = new URL(PATHS.PATIENTS + '/' + row._id, host)
    window.location.href = cookies.tokens
      ? `${patientUrl.href}?cookies=${cookies.tokens}&scope=${row.scope}`
      : patientUrl.href
  }

  return (
    <Box>
      <Box sx={{display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap'}}>
        <SearchBar
          value={search}
          onChange={handleSearchChange}
          placeholder="Cerca per nome o email..."
          sx={{flex: '1 1 240px', maxWidth: 360}}
        />
        <Box sx={{ml: 'auto'}}>
          <Filters config={filterConfig} onSubmit={handleFiltersSubmit} />
        </Box>
      </Box>

      <DataTable<PatientRecord>
        columns={columns}
        rows={pagedPatients}
        keyField="_id"
        loading={loadStatus === STATUS.PENDING}
        emptyMessage="Nessun paziente trovato"
        onRowClick={handleRowClick}
        totalElements={filteredPatients.length}
        pagination={pagination}
        onPaginationChange={setPagination}
      />
    </Box>
  )
}
